import { imageUtils } from './imageUtils'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import type { LeftoverPhoto } from './filterLeftovers'

interface FilterProcessingQueue {
  photoUrl: string
  photoId: string
  filterId: string
}

/** Photos the post-capture Filter page ran out of time for, still being filtered. */
interface LeftoverFilterJob {
  sessionId: string
  filterId: string
  photoIds: string[]
  done: Promise<void>
}

export interface LeftoverWaitResult {
  /** False when the wait gave up before the job ended. */
  finished: boolean
  /** Leftover photos that have a filtered version now. */
  filtered: number
  total: number
}

/**
 * Per-photo bound for leftovers. Higher than the Filter page's 12 s: nobody is staring at a
 * modal while these run, and the slowest booth measured needed 17 s for one photo.
 */
export const LEFTOVER_PER_PHOTO_TIMEOUT_MS = 30000

const inflightKey = (photoId: string, filterId: string): string => `${photoId}:${filterId}`

/** Resolves when `work` settles or after `ms`, whichever is first. Never rejects. */
const settleWithin = async (work: Promise<unknown>, ms: number): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([
    work.then(
      () => undefined,
      () => undefined
    ),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, ms)
    })
  ])
  if (timer) clearTimeout(timer)
}

class BackgroundFilterProcessor {
  private queue: FilterProcessingQueue[] = []
  private isProcessing = false
  private maxConcurrent = 1 // Process 1 filter at a time for robustness
  /** Filter work started by processPhotoNow that has not settled yet, by photo and filter. */
  private inflight = new Map<string, Promise<void>>()
  private leftoverJob: LeftoverFilterJob | null = null

  async addToQueue(photoUrl: string, photoId: string, filterId: string): Promise<void> {
    this.queue.push({ photoUrl, photoId, filterId })

    if (!this.isProcessing) {
      this.processQueue()
    }
  }

  private async processQueue(): Promise<void> {
    if (this.queue.length === 0) {
      this.isProcessing = false
      return
    }

    this.isProcessing = true

    // Process items in batches
    const batch = this.queue.splice(0, this.maxConcurrent)

    const promises = batch.map(async (item) => {
      try {
        await this.processFilterForPhoto(item.photoUrl, item.photoId, item.filterId)
      } catch (error) {
        console.error(`Failed to process filter ${item.filterId} for photo ${item.photoId}:`, error)
      }
    })

    await Promise.all(promises)

    // Continue processing remaining items
    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), 100) // Small delay to prevent blocking
    } else {
      this.isProcessing = false
    }
  }

  private async processFilterForPhoto(
    photoUrl: string,
    photoId: string,
    filterId: string,
    inWorker = false
  ): Promise<void> {
    const { filters } = useConfigStore.getState()
    const filter = filters.find((f) => f._id === filterId)
    if (!filter) return

    try {
      // Read gallery once at start to get current item for processing
      const { gallery: initialGallery, setSessionState } = useSessionStateStore.getState()
      const currentItem = initialGallery.capturedItems?.find((item) => item.id === photoId)
      
      // Use originalPhotoUrl if available, otherwise use photoUrl
      // This ensures we always process the original unfiltered image
      const originalPhotoUrlToProcess = currentItem?.originalPhotoUrl || photoUrl
      
      // Apply filter to photo (supports both manual adjustments and LUT)
      const filteredPhotoUrl = inWorker
        ? await imageUtils.applyFilterToImageInWorker(originalPhotoUrlToProcess, filter)
        : await imageUtils.applyFilterToImage(originalPhotoUrlToProcess, filter)

      // Re-read fresh gallery state after async work to avoid clobbering concurrent updates
      const { gallery: freshGallery } = useSessionStateStore.getState()
      const updatedItems = (freshGallery.capturedItems || []).map((item) => {
        if (item.id === photoId) {
          return {
            ...item,
            // Store originalPhotoUrl if not already set (first time filtering this photo)
            originalPhotoUrl: item.originalPhotoUrl || photoUrl,
            filteredVersions: {
              ...(item.filteredVersions || {}),
              [filterId]: filteredPhotoUrl
            }
          }
        }
        return item
      })
      setSessionState({
        gallery: {
          capturedItems: updatedItems
        }
      })

      console.log(`✅ Filter ${filter.name} applied to photo ${photoId}`)
    } catch (error) {
      console.error(`Failed to apply filter ${filterId} to photo ${photoId}:`, error)
    }
  }

  // private async checkAndCreateGIF(filterId: string): Promise<void> {
  //   const { gallery, setSessionState } = useSessionStateStore.getState()
  //   const { filters } = useConfigStore.getState()

  //   const filter = filters.find((f) => f._id === filterId)
  //   if (!filter) return

  //   // Get all captured photos
  //   const allPhotos = gallery.capturedItems.filter((item) => item.photoUrl)

  //   // Check if all photos have this filter applied
  //   const filteredPhotos = allPhotos
  //     .map((item) => item.filteredVersions?.[filterId])
  //     .filter((url): url is string => Boolean(url))

  //   if (filteredPhotos.length === allPhotos.length && filteredPhotos.length > 0) {
  //     try {
  //       // All photos are filtered, create GIF
  //       const gifBlob = await imageUtils.createGIF(
  //         filteredPhotos,
  //         1000, // 1 second per frame
  //         undefined // No custom frame
  //       )
  //       const gifUrl = URL.createObjectURL(gifBlob)

  //       // Update filter cache
  //       setSessionState({
  //         gallery: {
  //           filterCache: {
  //             ...gallery.filterCache,
  //             [filterId]: {
  //               photos: filteredPhotos,
  //               gifUrl,
  //               isComplete: true
  //             }
  //           }
  //         }
  //       })
  //     } catch (error) {
  //       console.error(`Failed to create GIF for filter ${filterId}:`, error)
  //     }
  //   }
  // }

  async processAllFiltersForPhoto(photoUrl: string, photoId: string): Promise<void> {
    const { filters } = useConfigStore.getState()

    // Add all filters to queue for this photo
    for (const filter of filters) {
      await this.addToQueue(photoUrl, photoId, filter._id)
    }
  }

  /**
   * Process a single photo and await its completion, bounded by a hard timeout.
   * Returns true once a filtered version exists for the photo in the gallery.
   *
   * The post-capture Filter flow uses this instead of enqueue + busy-poll: a stalled image
   * fetch can no longer keep the ProcessingModal frozen for the whole queue window — it costs
   * at most `timeoutMs` for that one photo, then we move on (Select falls back to the original).
   *
   * `inWorker` bakes in a filter worker, off the main thread (same pixels, see
   * imageUtils.applyFilterToImageInWorker). Only the view Filter page and its leftovers ask
   * for it; every other caller keeps the main-thread bake.
   */
  async processPhotoNow(
    photoUrl: string,
    photoId: string,
    filterId: string,
    timeoutMs = 12000,
    options: { inWorker?: boolean } = {}
  ): Promise<boolean> {
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      await Promise.race([
        this.trackInflight(photoUrl, photoId, filterId, options.inWorker === true),
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error(`Filter processing timed out after ${timeoutMs}ms`)),
            timeoutMs
          )
        })
      ])
    } catch (error) {
      console.error(`processPhotoNow failed for photo ${photoId}:`, error)
    } finally {
      if (timer) clearTimeout(timer)
    }
    return this.hasFilteredVersion(photoId, filterId)
  }

  /**
   * Same work as before; it is only recorded until it settles, so finishLeftovers can wait on
   * a photo whose timeout already fired instead of filtering it a second time.
   */
  private trackInflight(
    photoUrl: string,
    photoId: string,
    filterId: string,
    inWorker: boolean
  ): Promise<void> {
    const key = inflightKey(photoId, filterId)
    const work = this.processFilterForPhoto(photoUrl, photoId, filterId, inWorker)
    this.inflight.set(key, work)
    const clear = (): void => {
      if (this.inflight.get(key) === work) this.inflight.delete(key)
    }
    work.then(clear, clear)
    return work
  }

  private hasFilteredVersion(photoId: string, filterId: string): boolean {
    const { gallery } = useSessionStateStore.getState()
    const item = gallery.capturedItems?.find((i) => i.id === photoId)
    return Boolean(item?.filteredVersions?.[filterId])
  }

  /**
   * Keep filtering, one at a time, the photos the post-capture Filter page ran out of time
   * for. Select waits on this job (waitForLeftovers) before the GIF, the photo upload and the
   * strip export, so a slow booth still delivers every photo filtered. A photo whose timeout
   * fired on the Filter page is still running: that work is awaited, not started again.
   */
  finishLeftovers(
    sessionId: string,
    photos: LeftoverPhoto[],
    filterId: string,
    perPhotoTimeoutMs = LEFTOVER_PER_PHOTO_TIMEOUT_MS
  ): void {
    if (!sessionId || photos.length === 0) return
    const run = async (): Promise<void> => {
      for (const photo of photos) {
        if (useSessionStateStore.getState().id !== sessionId) return
        if (this.hasFilteredVersion(photo.id, filterId)) continue
        // The bake runs in a worker, but when it has to fall back to the main thread each
        // photo blocks it; let taps and paint through in between.
        await new Promise((resolve) => setTimeout(resolve, 0))
        const running = this.inflight.get(inflightKey(photo.id, filterId))
        if (running) {
          await settleWithin(running, perPhotoTimeoutMs)
        } else {
          await this.processPhotoNow(photo.photoUrl, photo.id, filterId, perPhotoTimeoutMs, {
            inWorker: true
          })
        }
      }
    }
    const job: LeftoverFilterJob = {
      sessionId,
      filterId,
      photoIds: photos.map((p) => p.id),
      done: run().catch((error) => {
        console.error('[backgroundFilterProcessor] finishLeftovers failed:', error)
      })
    }
    this.leftoverJob = job
    void job.done.then(() => {
      if (this.leftoverJob === job) this.leftoverJob = null
    })
  }

  /** True while photos the Filter page left are still being filtered for this session. */
  hasLeftovers(sessionId: string | null | undefined): boolean {
    return Boolean(sessionId && this.leftoverJob?.sessionId === sessionId)
  }

  /**
   * Wait, at most `maxWaitMs`, for this session's leftover photos. Resolves at once with
   * `finished: true` when there is no such job.
   */
  async waitForLeftovers(
    sessionId: string | null | undefined,
    maxWaitMs: number
  ): Promise<LeftoverWaitResult> {
    const job = this.leftoverJob
    if (!sessionId || !job || job.sessionId !== sessionId) {
      return { finished: true, filtered: 0, total: 0 }
    }
    let ended = false
    void job.done.then(() => {
      ended = true
    })
    await settleWithin(job.done, maxWaitMs)
    const filtered = job.photoIds.filter((id) => this.hasFilteredVersion(id, job.filterId)).length
    return { finished: ended, filtered, total: job.photoIds.length }
  }

  getProcessingStatus(): { queueLength: number; isProcessing: boolean } {
    return {
      queueLength: this.queue.length,
      isProcessing: this.isProcessing
    }
  }

  clearQueue(): void {
    this.queue = []
    this.isProcessing = false
  }
}

export const backgroundFilterProcessor = new BackgroundFilterProcessor()
